import { resetSharedGLTFLoader, setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import { normalizeMirroredGlbScene } from '@utils/sceneObjects/normalizeMirroredGlbScene'
import { recordSceneCostGlb } from '@utils/debug/sceneCostProfiler'
import { getGlbParseConcurrency } from '../../viewport/readCapabilities'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader'

const urlCache = new Map<string, Promise<GLTF>>()

let activeParses = 0
const parseWaiters: Array<() => void> = []

async function acquireParseSlot(): Promise<void> {
  const limit = Math.max(1, getGlbParseConcurrency())
  if (activeParses >= limit) {
    await new Promise<void>((resolve) => {
      parseWaiters.push(resolve)
    })
  }
  activeParses += 1
}

function releaseParseSlot(): void {
  activeParses = Math.max(0, activeParses - 1)
  const next = parseWaiters.shift()
  if (next) next()
}

export function hasActiveGlbParses(): boolean {
  return activeParses > 0
}

function isDecoderFailure(error: unknown): boolean {
  const message = String((error as { message?: string })?.message || error || '')
  return /draco|decoder|worker|wasm|context/i.test(message)
}

async function enqueueGlbParse<T>(task: () => Promise<T>): Promise<T> {
  await acquireParseSlot()
  try {
    return await task()
  } finally {
    releaseParseSlot()
  }
}

export async function getSharedGLTFLoader() {
  return setGLTFLoader()
}

async function parseGlbFromUrl(url: string): Promise<GLTF> {
  const loader = await getSharedGLTFLoader()
  const fetchStart = performance.now()
  try {
    const res = await fetch(url, { mode: 'cors' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const buf = await res.arrayBuffer()
    const fetchMs = performance.now() - fetchStart
    const resourcePath = url.replace(/[^/?#]+(?=[?#]|$)/, '')
    const parseStart = performance.now()
    const gltf = await new Promise<GLTF>((resolve, reject) => {
      loader.parse(buf, resourcePath, resolve, reject)
    })
    normalizeMirroredGlbScene(gltf.scene)
    recordSceneCostGlb({
      url,
      cacheHit: false,
      bytes: buf.byteLength,
      fetchMs,
      parseMs: performance.now() - parseStart,
      root: gltf.scene,
    })
    return gltf
  } catch (primaryErr) {
    if (isDecoderFailure(primaryErr)) {
      resetSharedGLTFLoader()
    }
    try {
      const loaderRetry = await getSharedGLTFLoader()
      const parseStart = performance.now()
      const gltf = await loaderRetry.loadAsync(url)
      normalizeMirroredGlbScene(gltf.scene)
      recordSceneCostGlb({
        url,
        cacheHit: false,
        bytes: 0,
        fetchMs: 0,
        parseMs: performance.now() - parseStart,
        root: gltf.scene,
      })
      return gltf
    } catch (fallbackErr) {
      // console.warn('[glbLoaderCache] Fallo fetch+parse y loadAsync:', url, primaryErr, fallbackErr)
      throw fallbackErr
    }
  }
}

/** Carga GLB por URL con deduplicación (misma URL = una sola descarga + decode). */
export async function loadGlbCached(url: string): Promise<GLTF> {
  const key = String(url || '').trim()
  if (!key) {
    throw new Error('loadGlbCached: empty url')
  }

  const existing = urlCache.get(key)
  if (existing) {
    const gltf = await existing
    recordSceneCostGlb({
      url: key,
      cacheHit: true,
      bytes: 0,
      fetchMs: 0,
      parseMs: 0,
      root: gltf.scene,
    })
    return gltf
  }

  const promise = enqueueGlbParse(() => parseGlbFromUrl(key))
  urlCache.set(key, promise)
  promise.catch(() => {
    urlCache.delete(key)
  })
  return promise
}

/** Cache first; if that parse fails, retry once without reusing the rejected entry. */
export async function loadGlbPreferCache(url: string): Promise<GLTF> {
  const key = String(url || '').trim()
  if (!key) throw new Error('loadGlbPreferCache: empty url')
  try {
    return await loadGlbCached(key)
  } catch {
    const gltf = await enqueueGlbParse(() => parseGlbFromUrl(key))
    urlCache.set(key, Promise.resolve(gltf))
    return gltf
  }
}

export function clearGlbUrlCache(): void {
  urlCache.clear()
}

export function isGlbUrlCached(url: string): boolean {
  return urlCache.has(String(url || '').trim())
}
